/**
 * Surgical editing of SpreadsheetML worksheet parts.
 *
 * Even within a single part we avoid a parse-and-reserialise round trip. A
 * worksheet carries conditional formatting, data validation, merged ranges,
 * pane freezes, hyperlinks, autofilters and extension blocks (`<extLst>`) that
 * a cell-level model does not represent — rebuilding the XML from that model
 * would drop them. Replacing exactly the `<c>` element that changed leaves the
 * rest of the part byte-identical, so an unedited save reproduces the original
 * file exactly (§13, §24).
 *
 * `<c>` elements never nest, which is what makes locating one by scanning both
 * simple and reliable.
 */
import { escapeXml, unescapeXml } from '../ooxml/xml.js';

export { escapeXml, unescapeXml };

export interface CellRef {
  readonly ref: string;
  /** Zero-based. */
  readonly row: number;
  readonly col: number;
}

/** "B12" → { row: 11, col: 1 }. Returns null for a malformed reference. */
export function parseRef(ref: string): CellRef | null {
  const m = /^([A-Z]+)(\d+)$/.exec(ref.toUpperCase());
  if (!m) return null;
  const letters = m[1]!;
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col = col * 26 + (letters.charCodeAt(i) - 64);
  }
  const row = Number(m[2]);
  if (row < 1) return null;
  return { ref: ref.toUpperCase(), row: row - 1, col: col - 1 };
}

/** { row: 11, col: 1 } → "B12". */
export function formatRef(row: number, col: number): string {
  let n = col + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return `${letters}${row + 1}`;
}

export interface ElementSpan {
  readonly start: number;
  readonly end: number;
  readonly selfClosing: boolean;
}

/** Locate `<tag ...>` … `</tag>` (or a self-closing form) starting at or after `from`. */
export function findElement(xml: string, tag: string, from = 0): ElementSpan | null {
  const open = new RegExp(`<${tag}(?=[\\s/>])`, 'g');
  open.lastIndex = from;
  const m = open.exec(xml);
  if (!m) return null;

  const start = m.index;
  const tagEnd = findTagEnd(xml, start);
  if (tagEnd < 0) return null;

  if (xml[tagEnd - 1] === '/') {
    return { start, end: tagEnd + 1, selfClosing: true };
  }
  const close = xml.indexOf(`</${tag}>`, tagEnd);
  if (close < 0) return null;
  return { start, end: close + tag.length + 3, selfClosing: false };
}

/** Index of the `>` that ends the tag starting at `start`, quote-aware. */
function findTagEnd(xml: string, start: number): number {
  let quote: string | null = null;
  for (let i = start; i < xml.length; i++) {
    const ch = xml[i]!;
    if (quote) {
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '>') {
      return i;
    }
  }
  return -1;
}

/** Find the `<c>` element whose `r` attribute equals `ref`. */
export function findCell(xml: string, ref: string): ElementSpan | null {
  const target = ref.toUpperCase();
  let from = 0;
  for (;;) {
    const span = findElement(xml, 'c', from);
    if (!span) return null;
    const head = xml.slice(span.start, Math.min(span.start + 200, span.end));
    const attr = /\br="([^"]+)"/.exec(head);
    if (attr && attr[1]!.toUpperCase() === target) return span;
    from = span.start + 2;
  }
}

/** Find the `<row>` element whose `r` attribute equals the 1-based row number. */
export function findRow(xml: string, rowNumber: number): ElementSpan | null {
  let from = 0;
  for (;;) {
    const span = findElement(xml, 'row', from);
    if (!span) return null;
    const head = xml.slice(span.start, Math.min(span.start + 200, span.end));
    const attr = /\br="(\d+)"/.exec(head);
    if (attr && Number(attr[1]) === rowNumber) return span;
    from = span.start + 4;
  }
}

/** Attribute value from an element's opening tag. */
export function readAttribute(xml: string, span: ElementSpan, name: string): string | null {
  const tagEnd = findTagEnd(xml, span.start);
  if (tagEnd < 0) return null;
  const head = xml.slice(span.start, tagEnd + 1);
  const m = new RegExp(`\\b${name}="([^"]*)"`).exec(head);
  return m ? m[1]! : null;
}

export type CellValue =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'boolean'; readonly value: boolean }
  | { readonly kind: 'empty' };

export interface CellWriteOptions {
  /** Style index from the original cell, preserved so formatting survives. */
  readonly styleIndex?: string | null;
}

/** Build a `<c>` element for a value. */
export function buildCell(ref: string, value: CellValue, options: CellWriteOptions = {}): string {
  const s = options.styleIndex ? ` s="${escapeXml(options.styleIndex)}"` : '';

  switch (value.kind) {
    case 'number':
      return `<c r="${ref}"${s}><v>${value.value}</v></c>`;
    case 'boolean':
      return `<c r="${ref}"${s} t="b"><v>${value.value ? 1 : 0}</v></c>`;
    case 'text':
      // Inline strings rather than the shared-string table: appending to
      // sharedStrings.xml would shift indices that other parts reference, and
      // the count attributes there must stay consistent. Inline is what every
      // consumer accepts and it touches nothing outside this cell.
      return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${escapeXml(value.text)}</t></is></c>`;
    case 'empty':
      return `<c r="${ref}"${s}/>`;
    default:
      return `<c r="${ref}"${s}/>`;
  }
}

export interface CellEdit {
  readonly ref: string;
  readonly value: CellValue;
}

export interface SheetEditResult {
  readonly xml: string;
  /** Refs whose formula was removed because a literal value replaced it. */
  readonly formulasReplaced: readonly string[];
  /** Refs that could not be placed. */
  readonly failed: readonly string[];
}

/**
 * Apply cell edits to a worksheet part.
 *
 * Edits are applied from the end of the document backwards so that each
 * replacement cannot invalidate the offsets of the ones still to come.
 */
export function applyCellEdits(xml: string, edits: readonly CellEdit[]): SheetEditResult {
  const formulasReplaced: string[] = [];
  const failed: string[] = [];

  interface Planned {
    readonly span: ElementSpan | null;
    readonly ref: string;
    readonly replacement: string;
    readonly insertAt: number | null;
  }

  const planned: Planned[] = [];

  for (const edit of edits) {
    const parsed = parseRef(edit.ref);
    if (!parsed) { failed.push(edit.ref); continue; }
    const ref = parsed.ref;

    const existing = findCell(xml, ref);
    if (existing) {
      const body = xml.slice(existing.start, existing.end);
      if (body.includes('<f')) formulasReplaced.push(ref);
      const styleIndex = readAttribute(xml, existing, 's');
      planned.push({
        span: existing, ref,
        replacement: buildCell(ref, edit.value, { styleIndex }),
        insertAt: null,
      });
      continue;
    }

    const insertion = planInsertion(xml, parsed, edit.value);
    if (!insertion) { failed.push(edit.ref); continue; }
    planned.push({ span: null, ref, replacement: insertion.text, insertAt: insertion.at });
  }

  // Highest offset first.
  planned.sort((a, b) => (b.span?.start ?? b.insertAt ?? 0) - (a.span?.start ?? a.insertAt ?? 0));

  let out = xml;
  for (const p of planned) {
    if (p.span) out = out.slice(0, p.span.start) + p.replacement + out.slice(p.span.end);
    else if (p.insertAt !== null) out = out.slice(0, p.insertAt) + p.replacement + out.slice(p.insertAt);
  }

  return { xml: out, formulasReplaced, failed };
}

/**
 * Where a new cell belongs.
 *
 * Excel requires `<c>` elements to appear in ascending column order within a
 * row, and `<row>` elements in ascending row order — a file that violates that
 * is repaired-on-open at best and rejected at worst.
 */
function planInsertion(
  xml: string, target: CellRef, value: CellValue,
): { readonly text: string; readonly at: number } | null {
  const cellXml = buildCell(target.ref, value);
  const rowNumber = target.row + 1;
  const rowSpan = findRow(xml, rowNumber);

  if (rowSpan) {
    if (rowSpan.selfClosing) {
      // `<row r="3"/>` — reopen it around the new cell.
      const tag = xml.slice(rowSpan.start, rowSpan.end);
      const opening = `${tag.slice(0, -2)}>`;
      return { text: `${opening}${cellXml}</row>`, at: rowSpan.start };
    }
    // Insert before the first cell with a higher column, else before </row>.
    let from = rowSpan.start;
    for (;;) {
      const cell = findElement(xml, 'c', from);
      if (!cell || cell.start > rowSpan.end) break;
      const ref = readAttribute(xml, cell, 'r');
      const parsed = ref ? parseRef(ref) : null;
      if (parsed && parsed.col > target.col) return { text: cellXml, at: cell.start };
      from = cell.end;
    }
    return { text: cellXml, at: rowSpan.end - '</row>'.length };
  }

  // The row does not exist: place it in ascending order inside <sheetData>.
  const sheetData = findElement(xml, 'sheetData');
  if (!sheetData) return null;
  const newRow = `<row r="${rowNumber}">${cellXml}</row>`;

  if (sheetData.selfClosing) {
    const tag = xml.slice(sheetData.start, sheetData.end);
    return { text: `${tag.slice(0, -2)}>${newRow}</sheetData>`, at: sheetData.start };
  }

  let from = sheetData.start;
  for (;;) {
    const row = findElement(xml, 'row', from);
    if (!row || row.start > sheetData.end) break;
    const r = readAttribute(xml, row, 'r');
    if (r && Number(r) > rowNumber) return { text: newRow, at: row.start };
    from = row.end;
  }
  return { text: newRow, at: sheetData.end - '</sheetData>'.length };
}
